import { message } from 'antd';
import { concat, filter, find, hasIn, toString } from 'lodash';
import moment from 'moment';
import { createContext } from 'react';

import { create, getAll } from '@/services/api';
import {
  getById,
  queryApiData,
  queryAutoComplete,
  queryDataSelect,
  queryDbConnections,
  queryGoToAction,
  queryPatternDefinition,
  queryPosition,
  queryProcessGroup,
  queryUser,
  queryUserGroup,
  toDownloadHTML,
  toExport,
  toImport,
} from '@/services/global';
import { CoreFormula } from '@/utils/core/business-logic';
import { CoreEnums } from '@/utils/domain/constants';
import { formatUTCLocal, toPaginate, translate } from '@/utils/infrastructure';

export const GlobalContext = createContext({});

const { FormFieldStringType, FormFieldType, TaskListType, UserCustomTaskListType } = CoreEnums;

const notification = { visible: false, type: 'success', message: '' };
const meta = { page: 1, limit: 5, count: 0, totalPages: 0 };

// to emulate model effect loading
const loading = { effects: {}, models: {} };

const initialState = {
  collapsed: false,
  showMobileMenu: false,
  loading,
  notices: [],
  pageSize: 5,
  searchCriteria: null,
  searchCriteriaLoading: false,
  showGlobalSearch: false,
  autoComplete: [],
  modalConfirm: false,
  modalConfirmDrawer: false,
  notificationGlobal: notification,
  notificationForm: notification,
  deleteId: null,
  isActive: '',
  orderBy: '',
  validationErrors: [],
  modelDelete: null,
  processGroups: [],
  breadcrumb: {},
  currentFile: null,
  shortenedUrl: null,
  filtersDrawerVisible: false,
  globalFilters: {}, // Changed to object to support filters by modelName
  ongoingOps: { count: 0, counter: 0 },
  // to store all remote content handled in forms
  apiData: {},
  loadingItem: false,
  patternDefinition: {},
  collapsedProcessConfig: false,
  processConfig: false,
  activeDrawer: false,
  userAddList: [],
  scheduledTask: false,
  goToAction: null,
  // to share error messages with global pages
  globalErrorMessage: null,
};

const GlobalModel = {
  namespace: 'global',
  state: initialState,
  effects: {
    *notices({ payload }, { put }) {
      yield put({ type: 'queryNotices', payload });
    },

    *willRefreshToken({ payload }, { put }) {
      yield put({ type: 'queryWillRefreshToken', payload });
    },

    *isCmdRunning({ payload }, { put }) {
      yield put({ type: 'queryCmdRunning', payload });
    },

    *uploading({ payload }, { put }) {
      yield put({ type: 'queryUploading', payload });
    },

    *loading({ payload }, { put }) {
      yield put({ type: 'queryLoading', payload });
    },

    *globalFiltersState({ payload, modelName }, { put }) {
      yield put({ type: 'queryGlobalFilters', payload: { filters: payload, modelName } });
    },

    *breadcrumb({ payload }, { put }) {
      yield put({ type: 'queryBreadcrumb', payload });
    },

    *toggleMobileMenu({ payload }, { put }) {
      yield put({ type: 'showMobileMenu', payload });
    },

    *globalSearchState({ payload }, { put }) {
      yield put({ type: 'globalSearch', payload });
    },

    *changePageSizeState({ payload }, { put }) {
      yield put({ type: 'changePageSize', payload });
    },

    *tableSearchState({ payload }, { put }) {
      yield put({ type: 'tableSearch', payload });
    },

    *tableSearchStateOnblur({ payload = 'tableSearchOnBlur' }, { put }) {
      yield put({ type: payload });
    },

    *fetchDataAutoComplete({ payload, url }, { call, put }) {
      try {
        const table = yield call(queryAutoComplete, payload, url);
        yield put({ type: 'queryAutoComplete', payload: table });
      } catch (e) {}
    },

    *tasksCountPending({ payload }, { call, put }) {
      try {
        const res = yield call(getAll, { route: 'tasks/countPending', ...payload });
        yield put({ type: 'queryCountPending', payload: res });
      } catch (e) {}
    },

    *clearDataAutoComplete(_, { put }) {
      yield put({ type: 'queryClearAutoComplete' });
    },

    *fetchDataPosition({ payload }, { call, put }) {
      try {
        const response = yield call(queryPosition, payload);
        yield put({ type: 'queryPosition', payload: response });
      } catch (e) {}
    },

    *fetchDataBoss({ payload }, { call, put }) {
      try {
        const response = yield call(queryUser, payload);
        yield put({
          type: 'queryBoss',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchDataUser({ payload }, { call, put }) {
      try {
        const response = yield call(queryUser, payload);
        yield put({ type: 'queryUser', payload: response });
      } catch (e) {}
    },

    *fetchDataSelect({ payload, route, name }, { call, put }) {
      try {
        yield put({ type: 'queryDataSelectSearch', name });
        const response = yield call(queryDataSelect, payload, route);
        yield put({ type: 'queryDataSelect', payload: response, params: payload, name });
      } catch (e) {
        yield put({ type: 'queryDataSelectSearch', name });
      }
    },

    *fetchDataUsersGroup({ payload }, { call, put }) {
      try {
        const response = yield call(queryUserGroup, payload);
        yield put({ type: 'queryUserGroup', payload: response });
      } catch (e) {}
    },

    *fetchDataParentProcessGroup({ payload }, { call, put }) {
      try {
        const response = yield call(queryProcessGroup, payload);
        yield put({
          type: 'queryProcessGroup',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchDataDbConnections({ payload }, { call, put }) {
      try {
        const response = yield call(queryDbConnections, payload);
        yield put({
          type: 'queryDbConnections',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchApiData({ payload }, { call, put }) {
      try {
        if (payload?.deeps?.length > 0) {
          yield put({ type: 'cleanApiData', payload: payload.deeps });
        }

        yield put({
          type: 'queryApiData',
          payload: { ...payload, loading: true },
        });

        const data = yield call(queryApiData, payload);
        yield put({
          type: 'queryApiData',
          payload: { data, ...payload, loading: false },
        });
      } catch (e) {
        yield put({
          type: 'queryApiData',
          payload: { ...payload, loading: false },
        });
      }
    },

    *fetchTableRecords({ payload }, { call, put }) {
      try {
        yield put({
          type: 'queryTableRecords',
          payload: { ...payload, loading: true },
        });

        const data = yield call(queryApiData, payload);
        yield put({
          type: 'queryTableRecords',
          payload: { ...payload, data },
        });
      } catch (e) {
        if (e?.validationErrors) {
          yield put({
            type: 'global/validationErrors',
            payload: e.validationErrors,
          });
        }

        yield put({
          type: 'queryTableRecords',
          payload: { ...payload, loading: false },
        });
      }
    },

    *fetchDataPatternDefinition({ payload }, { call, put }) {
      try {
        const data = yield call(queryPatternDefinition, payload);
        yield put({ type: 'patternDefinitions', payload: { data } });
      } catch (e) {}
    },

    *resetApiData({ payload }, { put }) {
      yield put({ type: 'cleanApiData', payload });
    },

    *hideNotification(_, { put }) {
      yield put({
        type: 'hideNotifications',
        payload: {
          visible: false,
          type: 'success',
          message: '',
        },
      });
    },

    *hideNotificationGlobal(_, { put }) {
      yield put({
        type: 'hideNotificationsGlobal',
        payload: {
          visible: false,
          type: 'success',
          message: '',
        },
      });
    },

    *hideNotificationForm(_, { put }) {
      yield put({
        type: 'hideNotificationsForm',
        payload: {
          visible: false,
          type: 'success',
          message: '',
        },
      });
    },

    *modalConfirm({ payload, state, id, modelDelete }, { put }) {
      yield put({
        type: 'showModalConfirm',
        modalConfirm: payload?.state || state,
        modelDelete: payload?.modelDelete || modelDelete,
        id: payload?.id || id,
      });
    },

    *modalConfirmDrawer({ payload, state, id, modelDelete }, { put }) {
      yield put({
        type: 'showModalConfirm',
        modalConfirmDrawer: payload?.state || state,
        modelDelete: payload?.modelDelete || modelDelete,
        id: payload?.id || id,
      });
    },

    *isActive({ payload }, { put }) {
      yield put({ type: 'isActiveSelect', payload });
    },

    *validationForm({ payload }, { put }) {
      yield put({ type: 'validateForm', payload });
    },

    *clearValidationForm(_, { put }) {
      yield put({ type: 'clearValidateForm' });
      yield put({ type: 'hideNotificationForm' });
    },

    *resetGlobal(_, { put }) {
      yield put({ type: 'resetGlobals' });
    },

    *changeCollapsedProcessConfig({ payload }, { put }) {
      yield put({ type: 'collapsedProcessConfig', payload });
    },

    *showProcessConfig({ payload }, { put }) {
      yield put({ type: 'processConfig', payload });
      yield put({ type: 'changeLayoutCollapsed', payload: payload });
    },

    *activeDrawerComponent({ payload }, { put }) {
      yield put({ type: 'activeDrawer', payload });
    },

    *addUserList({ payload }, { put }) {
      yield put({ type: 'addUserListNotification', payload });
    },

    *editUserList({ payload }, { put }) {
      yield put({ type: 'editUserListNotification', payload });
    },

    *deleteUserList({ payload }, { put }) {
      yield put({ type: 'deleteUserListNotification', payload });
    },

    *clearUserList({ payload = 'clearUserListNotification' }, { put }) {
      yield put({ type: payload });
    },

    *export({ payload }, { call, put }) {
      try {
        const res = yield call(toExport, payload);
        if (res.scheduledTask || (res.message && !res.content && !res.contentType)) {
          yield put({ type: 'queryExport', payload: res.message });
        } else {
          const pre = `data:${res.contentType};base64,${res.content}`;
          const a = document.createElement('a');
          a.setAttribute('download', res.fileName);
          a.setAttribute('href', pre);
          a.style.display = 'none';
          document.body.appendChild(a);
          a.click();
          a?.remove();
        }
      } catch (e) {}
    },

    *downloadHTML({ payload }, { call }) {
      try {
        const res = yield call(toDownloadHTML, payload);
        const blob = new Blob([res], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = payload.params.name + '-email.html';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {}
    },

    *downloadZIP({ payload }, { call }) {
      try {
        const response = yield call(toDownloadHTML, payload);
        const url = URL.createObjectURL(response);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${payload.params.name}.zip`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (e) {}
    },

    *directDownload({ payload }) {
      try {
        const { fileName, downloadUrl, storagePublicUrl, documentTemporaryUrl } = payload;
        const a = document.createElement('a');
        a.setAttribute('download', fileName);
        a.setAttribute('href', downloadUrl ?? storagePublicUrl ?? documentTemporaryUrl);
        a.setAttribute('target', '_blank');
        a.setAttribute('rel', 'noreferrer');
        a.click();
        a?.remove();
      } catch (e) {}
    },

    *import({ payload }, { call, put }) {
      try {
        const { source, ...params } = payload;
        const res = yield call(toImport, params);
        yield put({
          type: 'queryImport',
          payload: { ...res, ...payload },
        });
      } catch (e) {}
    },

    *filePreview({ payload }, { put }) {
      yield put({ type: 'queryFilePreview', payload });
    },

    *shortenedUrl({ payload }, { call, put }) {
      try {
        const params = { route: 'shortenedUrl/originalUrl', id: payload };
        const res = yield call(getById, params);
        yield put({
          type: 'queryShortRedirect',
          payload: { code: payload, ...res },
        });
      } catch (e) {}
    },

    *goToAction({ payload }, { call, put }) {
      try {
        const res = yield call(queryGoToAction, payload);
        yield put({ type: 'queryGoToAction', payload: res });
      } catch (e) {}
    },

    *toggleDrawerFilters({ payload }, { put }) {
      yield put({ type: 'queryDrawerFilter', payload });
    },

    *withError({ payload }, { put }) {
      yield put({ type: 'queryGlobalError', payload });
    },

    *cleanup({ payload }, { put }) {
      yield put({ type: 'queryState', payload });
    },

    *fetchDataProcess({ payload }, { call, put }) {
      try {
        const params = {
          route: 'process',
          method: 'get',
          ...payload,
        };
        const response = yield call(getAll, params);
        yield put({
          type: 'queryProcess',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchDataReports({ payload }, { call, put }) {
      try {
        const params = {
          route: 'report',
          method: 'get',
          ...payload,
        };
        const response = yield call(getAll, params);
        yield put({
          type: 'queryReports',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchDataList({ payload }, { call, put }) {
      try {
        const params = {
          route: 'customList',
          method: 'get',
          ...payload,
        };
        const response = yield call(getAll, params);
        yield put({
          type: 'queryDataList',
          payload: response,
        });
      } catch (e) {}
    },

    *fetchDataEntities({ payload }, { call, put }) {
      try {
        const params = {
          route: 'dynamicEntity',
          method: 'get',
          ...payload,
        };
        const response = yield call(getAll, params);
        yield put({
          type: 'queryDataEntities',
          payload: response,
        });
      } catch (e) {}
    },

    *securityCode({ payload }, { call }) {
      try {
        yield call(create, { route: `processEngine/sendSecurityCode/${payload}` });
        message.success(translate('global.message.securityCode'));
      } catch (e) {}
    },

    *ongoingOps({ payload }, { put }) {
      yield put({ type: 'queryOngoingOps', payload });
    },

    *validationErrors({ payload }, { put }) {
      yield put({ type: 'queryValidationErrors', payload });
    },
  },
  reducers: {
    queryNotices(state, { payload }) {
      const notices = JSON.parse(localStorage.getItem('x-notices') || '[]');

      if (_.isString(payload)) {
        const index = notices.findIndex((item) => item.id === payload);
        if (index !== -1) notices.splice(index, 1);
      } else notices.push(payload);

      localStorage.setItem('x-notices', JSON.stringify(notices));

      return {
        ...state,
        notices,
      };
    },

    queryCountPending(state, { payload }) {
      if (
        payload.listType === TaskListType.MyDay ||
        payload.listType === TaskListType.Favorites ||
        payload.taskListType === UserCustomTaskListType.FilterByTaskImportances
      ) {
        const tasksCountPending = [...state.tasksCountPending];
        const listIndex = tasksCountPending.findIndex((o) => o.listType === payload.listType);

        if (listIndex === -1) {
          return state;
        }

        const listItem = tasksCountPending[listIndex];

        if (payload.taskListType) {
          const taskListIndex = listItem?.counts?.findIndex(
            (o) => o.taskListType === payload.taskListType,
          );

          if (taskListIndex === -1) {
            return state;
          }

          const taskListItem = listItem?.counts?.[taskListIndex];
          const importance = taskListItem.filters?.importances?.items ?? [];
          const isChild = importance?.some?.((o) => +o.id === +payload.value);
          const isOld = importance?.some?.((o) => +o.id === +payload.previous);

          if (!isChild && !isOld) {
            return state;
          }

          tasksCountPending[listIndex].counts[taskListIndex] = {
            ...taskListItem,
            count: (taskListItem.count || 0) + (isChild ? 1 : 0) - (isOld ? 1 : 0),
          };
        } else {
          tasksCountPending[listIndex] = {
            ...listItem,
            count: (listItem.count || 0) + (+payload.value || -1),
          };
        }

        return {
          ...state,
          tasksCountPending,
        };
      }

      return {
        ...state,
        tasksCountPending: payload,
      };
    },

    queryWillRefreshToken(state) {
      return {
        ...state,
        willRefreshToken: true,
      };
    },

    resetRefreshToken(state) {
      return {
        ...state,
        willRefreshToken: false,
      };
    },

    queryCmdRunning(state, { payload }) {
      return {
        ...state,
        globCmdRunning: payload && {
          ...state.globCmdRunning,
          [payload.rowIndex]: payload.route || payload.command,
        },
      };
    },

    queryGlobalFilters(state, { payload }) {
      const { filters, modelName } = payload;

      // If no modelName, store filters globally (backward compatibility)
      if (!modelName) {
        return {
          ...state,
          globalFilters: filters,
        };
      }

      // Store filters by modelName
      const newGlobalFilters = {
        ...state.globalFilters,
        [modelName]: filters,
      };

      return {
        ...state,
        globalFilters: newGlobalFilters,
      };
    },

    queryBreadcrumb(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },

    showMobileMenu(state, { payload }) {
      return {
        ...state,
        showMobileMenu: payload ?? false,
      };
    },

    queryShortRedirect(state, { payload }) {
      return {
        ...state,
        shortenedUrl: payload,
      };
    },

    addUserListNotification(state, { payload }) {
      return {
        ...state,
        userAddList: concat(state.userAddList, {
          userId: payload,
          fullName: find(state.apiData.userId.data, (l) => l.id === payload).label,
        }),
      };
    },

    editUserListNotification(state, { payload }) {
      return {
        ...state,
        userAddList: payload,
      };
    },

    deleteUserListNotification(state, { payload }) {
      return {
        ...state,
        userAddList: filter(state.userAddList, (l) => l.userId !== payload),
      };
    },

    clearUserListNotification(state) {
      return {
        ...state,
        userAddList: [],
      };
    },

    changeLayoutCollapsed(state, { payload }) {
      return { ...state, collapsed: payload };
    },

    collapsedProcessConfig(state, { payload }) {
      return { ...state, collapsedProcessConfig: payload };
    },

    processConfig(state, { payload }) {
      return { ...state, processConfig: payload };
    },

    queryDbConnections(state, { payload }) {
      return {
        ...state,
        databaseConnectionId: payload.map(({ id, connectionName }) => ({
          label: connectionName,
          value: id,
        })),
      };
    },

    globalSearch(state, { payload }) {
      return {
        ...state,
        showGlobalSearch: payload,
      };
    },

    changePageSize(state, { payload }) {
      return {
        ...state,
        pageSize: payload,
      };
    },

    tableSearch(state, { payload }) {
      return {
        ...state,
        searchCriteria: payload,
        searchCriteriaLoading: true,
      };
    },

    tableSearchOnBlur(state) {
      return {
        ...state,
        searchCriteriaLoading: false,
      };
    },

    queryAutoComplete(state, { payload }) {
      return {
        ...state,
        autoComplete: payload,
      };
    },

    queryClearAutoComplete(state) {
      return {
        ...state,
        autoComplete: [],
      };
    },

    queryPosition(state, { payload }) {
      return {
        ...state,
        positionId: payload.map((item) => {
          return { label: item.name, value: item.id };
        }),
      };
    },

    queryBoss(state, { payload }) {
      return {
        ...state,
        userBossId: payload.map((item) => {
          return { label: item.fullName, value: item.id };
        }),
      };
    },

    queryProcess(state, { payload }) {
      return {
        ...state,
        processId: payload.map((item) => {
          return { label: item.name, value: item.id };
        }),
      };
    },

    queryReports(state, { payload }) {
      return {
        ...state,
        reportId: payload.map((item) => {
          return { label: item.title, value: item.id };
        }),
      };
    },

    queryDataList(state, { payload }) {
      return {
        ...state,
        listId: payload.map((item) => {
          return { label: item.name, value: item.id };
        }),
      };
    },

    queryDataEntities(state, { payload }) {
      return {
        ...state,
        entityId: payload.map((item) => {
          return { label: item.displayName, value: item.id };
        }),
      };
    },

    queryUser(state, { payload }) {
      return {
        ...state,
        userId: payload.map((item) => {
          return { label: item.fullName, value: item.id };
        }),
      };
    },

    queryDataSelect(state, { payload, params, name }) {
      const select = params.select.split(',');
      return {
        ...state,
        [name]: (Array.isArray(payload) ? payload : payload.items)
          .filter((row) => row.id.length > 0)
          .map((item) => {
            return {
              ...item,
              label: item[select[1]],
              value: item[select[0]],
            };
          }),
        [`${name}search`]: false,
      };
    },

    queryDataSelectSearch(state, { name }) {
      return {
        ...state,
        [name]: [],
        [`${name}search`]: true,
      };
    },

    queryUserGroup(state, { payload }) {
      return {
        ...state,
        userGroupId: payload.map((item) => {
          return { label: item.name, value: item.id };
        }),
      };
    },

    queryProcessGroup(state, { payload }) {
      return {
        ...state,
        parentProcessGroupId: payload.map((item) => {
          return { label: item.name, value: item.id };
        }),
      };
    },

    queryTableRecords(state, { payload }) {
      const currData = state.apiData?.[payload.source];

      const pagination = toPaginate({
        ...(currData?.pagination ?? meta),
        ...(payload.params?.page && { page: payload.params.page }),
        ...(payload.params?.limit && { limit: payload.params.limit }),
        totalCount: payload.data?.totalCount ?? 0,
      });

      return {
        ...state,
        apiData: {
          ...state.apiData,
          [payload.source]: {
            ...state.apiData[payload.source],
            loading: payload.loading ?? false,
            fetched: !!payload.data,
            data: payload.data?.items ?? [],
            imported: false,
            errors: null,
            errorMessage: null,
            warnings: null,
            pagination,
          },
        },
      };
    },

    queryApiData(state, { payload }) {
      const src = payload.lazy ? payload.data?.items : payload.data;

      let data = _.map(src, (o) => {
        switch (payload.displayConfig?.type) {
          case FormFieldType.Date:
          case FormFieldStringType.Date: {
            const format = CoreFormula._CleanMomentFormat(payload.displayConfig.format);
            const date = format ? moment(o.text).format(format) : o.text;
            return { label: date, value: date, format };
          }

          case FormFieldStringType.DateTime:
          case FormFieldType.DateTime: {
            const format = CoreFormula._CleanMomentFormat(payload.displayConfig.format);
            const date = formatUTCLocal(o.text, format);
            return { label: date, value: date };
          }

          default: {
            const preferred = payload.displayField && o[payload.displayField];
            return {
              ...o,
              label:
                preferred ||
                o.name ||
                o.displayName ||
                o.value ||
                o.fullName ||
                o.displayText ||
                o.internalName ||
                o.databaseName ||
                o.text,
              value: o.id,
            };
          }
        }
      });

      if (payload.lazy) {
        const currData = state.apiData[payload.source];

        const pagination = toPaginate({
          ...(currData?.pagination ?? meta),
          page: payload.params.page,
          limit: payload.params.limit,
          totalCount: payload.data?.totalCount ?? 0,
        });

        return {
          ...state,
          apiData: {
            ...state.apiData,
            [payload.source]: {
              loading: payload.data ? false : !!payload.loading,
              fetched: payload.data ? true : payload.loading ? false : true,
              maxCount: currData?.maxCount || payload.data?.totalCount || 0,
              data: payload.page === 1 ? data : [...(currData?.data ?? []), ...(data ?? [])],
              pagination,
            },
          },
        };
      }

      if (payload.transformLabel) {
        data = _.map(data, (o) => {
          const itemLabel = find(payload.arrayLabelsTransform, {
            value: o.label,
          });
          return {
            label: itemLabel ? translate(itemLabel.label) : o.label,
            value: itemLabel ? itemLabel.value : o.value,
          };
        });
      }

      return {
        ...state,
        apiData: {
          ...state.apiData,
          [payload.source]: {
            ...state.apiData[payload.source],
            loading: payload.data ? false : !!payload.loading,
            fetched: payload.data ? true : payload.loading ? false : true,
            data,
          },
        },
      };
    },

    cleanApiData(state, { payload = [] }) {
      const apiData = { ...state.apiData };

      payload.forEach((str) => {
        apiData[str] = {
          data: [],
          loading: false,
          fetched: false,
          ...(hasIn(apiData[str], 'pagination') && {
            pagination: meta,
            maxCount: apiData[str]?.maxCount ?? 0,
          }),
          imported: false,
          errors: null,
          errorMessage: null,
          warnings: null,
        };
      });

      return { ...state, apiData };
    },

    hideNotifications(state, { payload }) {
      return {
        ...state,
        notificationForm: payload,
        notificationGlobal: payload,
      };
    },

    hideNotificationsGlobal(state, { payload }) {
      return {
        ...state,
        notificationGlobal: payload,
      };
    },

    hideNotificationsForm(state, { payload }) {
      return {
        ...state,
        notificationForm: payload,
      };
    },

    showNotificationsGlobal(state, { payload, validationErrors = [] }) {
      return {
        ...state,
        notificationGlobal: payload,
        validationErrors,
      };
    },

    showNotificationsForm(state, { payload, validationErrors = [] }) {
      return {
        ...state,
        notificationForm: payload,
        validationErrors,
      };
    },

    validateForm(state, { payload }) {
      return {
        ...state,
        validationErrors: state.validationErrors.filter((item) => item.field !== payload),
      };
    },

    clearValidateForm(state) {
      return {
        ...state,
        validationErrors: [],
      };
    },

    showModalConfirm(
      state,
      { modalConfirm = false, modalConfirmDrawer = false, id = null, modelDelete = null },
    ) {
      return {
        ...state,
        modalConfirm,
        modalConfirmDrawer,
        deleteId: id,
        modelDelete,
      };
    },

    isActiveSelect(state, { payload }) {
      return {
        ...state,
        isActive: payload,
      };
    },

    orderBy(state, { payload }) {
      return {
        ...state,
        orderBy: payload,
      };
    },

    loadingItem(state, { payload }) {
      return {
        ...state,
        loadingItem: payload,
      };
    },

    patternDefinitions(state, { payload }) {
      return {
        ...state,
        patternDefinition: payload.data,
      };
    },

    activeDrawer(state, { payload }) {
      return { ...state, activeDrawer: payload.state };
    },

    queryExport(state, { payload }) {
      return {
        ...state,
        scheduledTask: payload,
      };
    },

    queryImport(state, { payload }) {
      if (payload.errorMessage || payload.errors?.length > 0) {
        return {
          ...state,
          apiData: {
            ...state.apiData,
            [payload.source]: {
              imported: false,
              ...state.apiData[payload.source],
              ..._.pick(payload, ['errorMessage', 'errors', 'warnings']),
            },
          },
        };
      }

      const source = payload.data?.items || payload.items || [];
      const totalCount = payload.data?.totalCount || payload.totalCount || 0;

      let data = [...(state.apiData[payload.source]?.data || [])];

      if (payload.cleanup) data = source;
      else {
        const temp = _.map(data, (rec) => {
          const item = _.find(source, { id: rec.id });
          const imported = _.toPairs(_.deepDiff(rec, item)).reduce((lhs, [key, { from, to }]) => {
            return {
              ...lhs,
              [key]: _.isNil(to) ? from : to,
            };
          }, {});

          return { ...rec, ...imported };
        });

        data = [...temp, ..._.filter(source, (rec) => !_.find(data, { id: rec.id }))];
      }

      return {
        ...state,
        apiData: {
          ...state.apiData,
          [payload.source]: {
            data,
            imported: true,
            pagination: toPaginate({ ...meta, totalCount }),
            warnings: payload.warnings,
          },
        },
      };
    },

    queryFilePreview(state, { payload }) {
      return {
        ...state,
        currentFile: payload,
      };
    },

    clearShortRedirect(state) {
      return {
        ...state,
        shortenedUrl: false,
      };
    },

    queryDrawerFilter(state, { payload }) {
      return {
        ...state,
        filtersDrawerVisible: payload ?? false,
        filtersContainerRef: payload?.containerRef || null,
      };
    },

    queryState(state, { payload }) {
      switch (payload) {
        case 'export':
          return {
            ...state,
            scheduledTask: false,
          };
        case 'gotoAction':
          return {
            ...state,
            goToAction: null,
          };
        default:
          return initialState;
      }
    },

    queryGlobalError(state, { payload }) {
      return {
        ...state,
        globalErrorMessage: payload ?? null,
      };
    },

    queryGoToAction(state, { payload }) {
      return {
        ...state,
        goToAction: payload,
      };
    },

    queryUploading(state, { payload }) {
      return { ...state, isUploading: payload };
    },

    queryLoading(state, { payload }) {
      return {
        ...state,
        loading: {
          ...state.loading,
          effects: {
            ...state.loading.effects,
            [payload]: !state.loading.effects[payload],
          },
        },
      };
    },

    queryOngoingOps(state, { payload }) {
      return {
        ...state,
        ongoingOps: {
          counter: payload ? state.ongoingOps.counter + payload.num : 0,
          count: payload ? payload.count : 0,
        },
      };
    },

    queryValidationErrors(state, { payload }) {
      return {
        ...state,
        ...(!payload && { validationErrors: [] }),
        ...(_.isArray(payload) && { validationErrors: payload }),
        ...(_.isString(payload) && {
          validationErrors: state.validationErrors?.filter((o) => o.field !== payload),
        }),
      };
    },

    resetGlobals(state) {
      return {
        ...state,
        searchCriteria: null,
        autoComplete: [],
        deleteId: null,
        isActive: '',
        orderBy: '',
        validationErrors: [],
        goToAction: null,
        modelDelete: null,
        processGroups: [],
        notificationGlobal: {
          visible: false,
          type: 'success',
          message: '',
        },
        notificationForm: {
          visible: false,
          type: 'success',
          message: '',
        },
      };
    },
  },

  subscriptions: {
    setup({ history, dispatch }) {
      // Subscribe history(url) change, trigger `load` action if pathname is `/`
      history.listen(({ pathname, search }) => {
        window.scrollTo(0, 0);

        // Removes 'x-logout-in-progress` from storage
        if (pathname.includes('/user/login')) {
          localStorage.removeItem('x-logout-in-progress');
          localStorage.removeItem('x-auth-token-refresh');
        }

        if (typeof window.ga !== 'undefined') {
          window.ga('send', 'pageview', pathname + search);
        }

        const portal = localStorage.getItem(`x-auth-companyPortalConfig`);
        if (portal) {
          try {
            dispatch({
              type: 'companyPortal/config',
              payload: JSON.parse(portal),
            });
          } catch (e) {}
        }
      });

      // to register all formulae into the global scope
      _.toPairs(CoreFormula).forEach(([k, fn]) => {
        if (!_.has(window, k)) {
          window[k] = fn;
        }
      });

      // to sync user company/environment after refresh token
      if (localStorage.getItem('x-auth-token-refresh') && localStorage.getItem('x-auth-token')) {
        localStorage.removeItem('x-auth-token-refresh');
        dispatch({ type: 'user/fetchCurrent', payload: true });
      }

      window.dispatch = dispatch; // set dispatch method to use it outside the view
      // to unregister all functions associated with the formulae calculations
      window.onbeforeunload = cleanupFormulae;
      function cleanupFormulae() {
        delete window.dispatch;
        _.keys(CoreFormula).forEach((k) => {
          delete window[k];
        });
        return null;
      }

      // Polyfill to add support to `crypto.randomUUID()` in older browsers
      if (!crypto.randomUUID) {
        crypto.randomUUID = function () {
          return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            const r = (Math.random() * 16) | 0;
            const v = c == 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
          });
        };
      }

      // to be sure that each tab has its own unique ID
      if (sessionStorage.getItem('x-tabID')) {
        window.tabID = sessionStorage.getItem('x-tabID');
        return;
      }

      const tabID = `tab-${crypto.randomUUID()}`;
      sessionStorage.setItem('x-tabID', tabID);
      window.tabID = tabID;
    },
  },
};

export default GlobalModel;
